import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  Chip,
  IconButton,
  Divider,
  Alert,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  BusinessOutlined as CorporateIcon,
  PersonOutlined as IndividualIcon,
  GroupsOutlined as EmployeeIcon,
  PublicOutlined as ThirdPartyIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  InfoOutlined as InfoIcon,
  AccountTree as OrgChartIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import AddShareholderModal from '../../../components/companies/AddShareholderModal';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import CompanyHierarchyChart, { buildHierarchyData } from '../../../components/charts/CompanyHierarchyChart';
import { calculateEtisalatOwnership } from '../../../data/companiesData';
import { csAdminApi } from '../../../services/api';
import {
  isThirdPartyShareholder,
  isGroupCompanyShareholder,
  isSideChartShareholder,
  getShareholderTypeLabel,
  getShareholderDisplayColors,
} from '../../../utils/shareholderTypes';
import {
  formatShareholdingPercent,
  parseShareholdingPercent,
} from '../../../utils/shareholdingPercent';

const ShareholdersTab = ({ companyId, companyName, shareholders = [], onRefresh }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedShareholder, setSelectedShareholder] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [shareholderToDelete, setShareholderToDelete] = useState(null);
  const [viewMode, setViewMode] = useState(0);

  const sideChartShareholders = shareholders.filter(isSideChartShareholder);

  // Calculate totals
  const totalPercentage = shareholders.reduce((sum, sh) => sum + (sh.percentage || 0), 0);
  const isValid = Math.abs(totalPercentage - 100) < 0.01; // Allow for floating point errors

  const getShareholderIcon = (type) => {
    if (isThirdPartyShareholder({ shareholderType: type })) {
      return <ThirdPartyIcon />;
    }
    switch (type?.toLowerCase()) {
      case 'corporate':
        return <CorporateIcon />;
      case 'individual':
      case 'private':
        return <IndividualIcon />;
      case 'employee':
      case 'management':
        return <EmployeeIcon />;
      default:
        return <IndividualIcon />;
    }
  };

  const getShareholderColor = (type, index = 0) => {
    const themed = getShareholderDisplayColors(type);
    if (isThirdPartyShareholder({ shareholderType: type })) {
      return themed;
    }
    const colorPairs = [
      { bg: '#FFEBEE', color: '#B71C1C' },
      { bg: '#E3F2FD', color: '#1565C0' },
      { bg: '#E8F5E9', color: '#2E7D32' },
      { bg: '#FFF3E0', color: '#E65100' },
      { bg: '#F3E5F5', color: '#6A1B9A' },
      { bg: '#E0F2F1', color: '#00695C' },
      { bg: '#FFCDD2', color: '#C62828' },
      { bg: '#E1F5FE', color: '#0277BD' },
    ];
    return colorPairs[index % colorPairs.length];
  };

  // Prepare data for pie chart
  const chartData = shareholders.map(sh => ({
    name: sh.shareholderName,
    value: sh.percentage,
    type: sh.shareholderType
  }));

  // More distinct color palette for chart - mix of reds, oranges, and complementary colors
  const COLORS = [
    '#B71C1C', // Dark red (primary Etisalat entity)
    '#1565C0', // Blue (government/institutional)
    '#2E7D32', // Green (public float)
    '#E65100', // Orange
    '#6A1B9A', // Purple
    '#00695C', // Teal
    '#C62828', // Medium red
    '#0277BD', // Light blue
  ];

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const handleAddShareholder = () => {
    setShowAddModal(true);
  };

  const handleEditShareholder = (shareholder) => {
    setSelectedShareholder(shareholder);
    setShowAddModal(true);
  };

  const handleDeleteShareholder = (shareholder) => {
    setShareholderToDelete(shareholder);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      if (shareholderToDelete?.id) {
        await csAdminApi.deleteCompanyShareholder(companyId, shareholderToDelete.id);
      }
    } catch (err) {
      console.error('Delete shareholder:', err);
    }
    setShowDeleteDialog(false);
    setShareholderToDelete(null);
    if (onRefresh) onRefresh();
  };

  const handleAddOrUpdateShareholder = async (shareholderData) => {
    {
      const payload = {
        name: shareholderData.shareholderName || shareholderData.name,
        type: (shareholderData.shareholderType || shareholderData.type || 'CORPORATE').toUpperCase(),
        percentage: parseShareholdingPercent(shareholderData.percentage),
        nationality: shareholderData.nationality,
        notes: shareholderData.notes,
        is_etisalat_entity: isThirdPartyShareholder(shareholderData) || !shareholderData.isEtisalatEntity
          ? 0
          : 1,
      };
      if (selectedShareholder?.id) {
        await csAdminApi.updateCompanyShareholder(companyId, selectedShareholder.id, payload);
      } else {
        await csAdminApi.createCompanyShareholder(companyId, payload);
      }
    }
    enqueueSnackbar(
      selectedShareholder?.id ? 'Shareholder updated' : 'Shareholder added',
      { variant: 'success' }
    );
    setShowAddModal(false);
    setSelectedShareholder(null);
    if (onRefresh) onRefresh();
  };

  const shareholderModals = (
    <>
      <AddShareholderModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedShareholder(null);
        }}
        onAdd={handleAddOrUpdateShareholder}
        companyId={companyId}
        companyName={companyName}
        existingShareholders={shareholders}
        editShareholder={selectedShareholder}
      />
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setShareholderToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Shareholder"
        message={`Are you sure you want to remove "${shareholderToDelete?.shareholderName}" from the shareholders list?`}
      />
    </>
  );

  if (!shareholders || shareholders.length === 0) {
    return (
      <>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Shareholding Structure</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddShareholder}
              size="small"
            >
              Add Shareholder
            </Button>
          </Box>

          <Box textAlign="center" py={6}>
            <CorporateIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="textSecondary" gutterBottom>
              No shareholders recorded
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Add shareholders to track ownership structure
            </Typography>
          </Box>
        </Paper>
        {shareholderModals}
      </>
    );
  }

  // Calculate Etisalat ownership using the isEtisalatEntity flag
  const etisalatOwnership = calculateEtisalatOwnership(shareholders);
  const isMember = etisalatOwnership > 50;

  const ownershipChartData = buildHierarchyData(
    {
      id: companyId,
      name: companyName,
      type: 'HOLDING',
      status: 'ACTIVE',
    },
    [],
    [],
    null,
    sideChartShareholders
  );

  const listAndChart = (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={viewMode} onChange={(e, v) => setViewMode(v)}>
          <Tab label="Summary" />
          <Tab
            label={`Ownership diagram${sideChartShareholders.length ? ` (${sideChartShareholders.length})` : ''}`}
            icon={<OrgChartIcon fontSize="small" />}
            iconPosition="start"
            disabled={sideChartShareholders.length === 0}
          />
        </Tabs>
      </Box>

      {viewMode === 1 && sideChartShareholders.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Third-party shareholders (blue) are shown beside the company. They are not part of your company registry.
          </Typography>
          <CompanyHierarchyChart
            nodes={ownershipChartData.nodes}
            edges={ownershipChartData.edges}
            height={420}
            showControls
          />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Ownership Distribution
            </Typography>
            <Box sx={{ height: 320, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="45%"
                    labelLine={false}
                    label={({ value }) => formatShareholdingPercent(value)}
                    outerRadius={70}
                    innerRadius={35}
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={3}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          isThirdPartyShareholder({ shareholderType: entry.type })
                            ? '#1565C0'
                            : COLORS[index % COLORS.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${value.toFixed(2)}%`, 'Ownership']}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 12 }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    layout="vertical"
                    wrapperStyle={{
                      paddingTop: 10,
                      fontSize: 11,
                      lineHeight: '1.6',
                    }}
                    formatter={(value) => (
                      <span style={{ color: '#424242', fontSize: 11 }}>
                        {value.length > 25 ? `${value.substring(0, 25)}...` : value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Shareholders ({shareholders.length})
            </Typography>

            <List sx={{ p: 0 }}>
              {shareholders.map((shareholder, index) => {
                const colors = getShareholderColor(shareholder.shareholderType, index);

                return (
                  <React.Fragment key={shareholder.id}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1.5,
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '8px',
                            bgcolor: colors.bg,
                            color: colors.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            position: 'relative',
                            border: `2px solid ${isThirdPartyShareholder(shareholder) ? '#1976D2' : COLORS[index % COLORS.length]}`,
                          }}
                        >
                          {getShareholderIcon(shareholder.shareholderType)}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight={600}>
                              {shareholder.shareholderName}
                            </Typography>
                            <Chip
                              label={getShareholderTypeLabel(shareholder.shareholderType)}
                              sx={{
                                bgcolor: colors.bg,
                                color: colors.color,
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: 18,
                                borderRadius: '9px',
                                letterSpacing: '0.5px',
                              }}
                              size="small"
                            />
                            {isThirdPartyShareholder(shareholder) && (
                              <Chip
                                label="Third party"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.6rem',
                                  bgcolor: '#E3F2FD',
                                  color: '#1565C0',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                            {isGroupCompanyShareholder(shareholder) && shareholder.isEtisalatEntity && (
                              <Chip
                                label="Group"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.6rem',
                                  bgcolor: '#FFEBEE',
                                  color: '#C62828',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {shareholder.nationality && (
                              <Typography variant="caption" color="textSecondary">
                                {isGroupCompanyShareholder(shareholder) || isThirdPartyShareholder(shareholder)
                                  ? `Country: ${shareholder.nationality}`
                                  : `Nationality: ${shareholder.nationality}`}
                              </Typography>
                            )}
                            <Typography variant="caption" color="textSecondary">
                              {shareholder.nationality ? '• ' : ''}Shares: {shareholder.shareCount?.toLocaleString() || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              • <strong style={{ color: COLORS[index % COLORS.length] }}>{formatShareholdingPercent(shareholder.percentage)}</strong>
                            </Typography>
                            {shareholder.acquisitionDate && (
                              <Typography variant="caption" color="textSecondary">
                                • Acquired: {formatDate(shareholder.acquisitionDate)}
                              </Typography>
                            )}
                            {shareholder.votingRights && (
                              <Chip
                                label="Voting Rights"
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: '0.6rem',
                                  bgcolor: '#FFEBEE',
                                  color: '#C62828',
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditShareholder(shareholder)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteShareholder(shareholder)}
                            title="Delete"
                            sx={{ color: '#C62828' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </ListItem>
                    {index < shareholders.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Grid>
        </Grid>
      )}
    </>
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">Shareholding Structure</Typography>
          <Typography variant="caption" color="textSecondary">
            {shareholders.length} shareholder{shareholders.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddShareholder}
          size="small"
        >
          Add Shareholder
        </Button>
      </Box>

      {!isValid && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<InfoIcon />}>
          <Typography variant="body2" fontWeight={500}>
            Total shareholding: {totalPercentage.toFixed(2)}%
          </Typography>
          <Typography variant="caption">
            {totalPercentage > 100
              ? 'Total exceeds 100%. Please adjust shareholding percentages.'
              : 'Total is less than 100%. Please add or adjust shareholding percentages.'}
          </Typography>
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            label={`Etisalat Ownership: ${etisalatOwnership.toFixed(1)}%`}
            sx={{
              bgcolor: '#FFEBEE',
              color: '#C62828',
              fontWeight: 600,
            }}
          />
          <Chip
            label={isMember ? 'ETISALAT MEMBER' : 'NON-MEMBER'}
            sx={{
              bgcolor: isMember ? '#B71C1C' : '#424242',
              color: 'white',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
            size="small"
          />
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: isValid ? '#2E7D32' : '#F57C00',
            fontWeight: 700,
          }}
        >
          {totalPercentage.toFixed(2)}%
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {listAndChart}

      {shareholderModals}
    </Paper>
  );
};

export default ShareholdersTab;
